import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useContent } from '../hooks/useContent';
import { CONFIG } from '../utils/config';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const { isDark } = useTheme();

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!email) {
      setFeedback({ type: 'error', message: content.feedback.missingEmail });
      return;
    }

    setIsSubmitting(true);
    setFeedback({ type: '', message: '' });

    try {
      const body = new URLSearchParams({ user_mail: email }).toString();
      const response = await fetch(`${CONFIG.BASE_URL}/request-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });

      const isJson = (response.headers.get('content-type') || '').includes('application/json');
      const data = isJson ? await response.json() : {};

      if (!response.ok) {
        throw new Error(data?.error || content.feedback.genericError);
      }

      setFeedback({
        type: 'success',
        message: content.feedback.success,
      });
      setEmail('');
    } catch (error) {
      setFeedback({
        type: 'error',
        message: error.message || content.feedback.requestError,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const { content, loading: contentLoading } = useContent('forgotPassword');

  if (contentLoading || !content) {

    return (
      <div className={`pt-16 min-h-screen flex items-center justify-center ${isDark ? 'bg-dark-900' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="pt-16 min-h-screen flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2">
            <img
              src="/images/logo.png"
              alt="Quikr.it Logo"
              className="w-40 h-20 object-contain"
            />
          </Link>

          <p className="text-gray-400 mt-2">
		{content.pageTitle} {content.pageSubtitle}
          </p>
        </div>

        <div className="bg-dark-800 p-8 rounded-xl border border-dark-700">
          <form onSubmit={handleSubmit} className="space-y-6">
            {feedback.message && (
              <div
                className={`px-4 py-3 rounded-lg text-sm border ${
                  feedback.type === 'success'
                    ? 'bg-green-900/40 border-green-700 text-green-200'
                    : 'bg-red-900/40 border-red-700 text-red-200'
                }`}
              >
                {feedback.message}
              </div>
            )}

            <div>
              <label htmlFor="forgot-email" className="block text-sm font-medium text-gray-300 mb-2">
		  {content.form.emailLabel}
              </label>
              <input
                id="forgot-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                placeholder={content.form.emailPlaceholder}
                required
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors duration-200"
            >
              {isSubmitting ? content.form.submitting : content.form.submitButton}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-400">
            <Link to="/login" className="text-primary-400 hover:text-primary-300">
		  {content.footerLink}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
